import React, { useState } from 'react';
import { Card, Row, Col, Button } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

/**
 * FinancialSummaryCard - A reusable component to display financial summary
 * 
 * @param {number} totalSales - Total sales amount
 * @param {number} totalCollected - Total collected amount (payments received)
 * @param {number} totalExpenses - Total expenses amount
 * @param {function} formatCurrency - Function to format currency values
 * @param {boolean} showCollected - Whether to show the "Collected" column (default: true, only shown if different from sales)
 */
const FinancialSummaryCard = ({
    totalSales = 0,
    totalCollected = 0,
    totalExpenses = 0,
    formatCurrency,
    showCollected = true
}) => {
    const { t } = useTranslation();
    const [showAmounts, setShowAmounts] = useState(true);

    const balance = totalCollected - totalExpenses;
    const showCollectedColumn = showCollected && totalSales !== totalCollected;

    return (
        <Card className="shadow-sm border-0">
            <Card.Body className="py-3 text-center">
                <div className="d-flex justify-content-center align-items-center mb-2 position-relative">
                    <div className="d-flex align-items-center gap-2">
                        <h6 className="text-muted text-uppercase mb-0" style={{ fontSize: '0.8rem' }}>
                            {t('salesPage.card.balance')}
                        </h6>
                        <div
                            className="fw-bold"
                            style={{
                                fontSize: 'clamp(0.9rem, 3vw, 1.2rem)',
                                color: balance >= 0 ? '#28a745' : '#dc3545'
                            }}
                        >
                            {showAmounts ? formatCurrency(balance) : '******'}
                        </div>
                    </div>
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-muted position-absolute"
                        style={{ right: 0 }}
                        onClick={() => setShowAmounts(!showAmounts)}
                        title={showAmounts ? t('common.hideValues', 'Ocultar valores') : t('common.showValues', 'Mostrar valores')}
                    >
                        {showAmounts ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                </div>
                <Row>
                    <Col xs={showCollectedColumn ? 4 : 6} className="border-end">
                        <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: '0.8rem' }}>
                            {t('salesPage.card.totalSales')}
                        </h6>
                        <div
                            className="fw-bold text-primary mb-0"
                            style={{ fontSize: 'clamp(0.9rem, 3vw, 1.3rem)', lineHeight: 1.2, whiteSpace: 'nowrap' }}
                        >
                            {showAmounts ? formatCurrency(totalSales) : '******'}
                        </div>
                    </Col>
                    {showCollectedColumn && (
                        <Col xs={4} className="border-end">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: '0.8rem' }}>
                                {t('salesPage.card.totalCollected')}
                            </h6>
                            <div
                                className="fw-bold text-success mb-0"
                                style={{ fontSize: 'clamp(0.9rem, 3vw, 1.3rem)', lineHeight: 1.2, whiteSpace: 'nowrap' }}
                            >
                                {showAmounts ? formatCurrency(totalCollected) : '******'}
                            </div>
                        </Col>
                    )}
                    <Col xs={showCollectedColumn ? 4 : 6}>
                        <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: '0.8rem' }}>
                            {t('salesPage.card.totalExpenses')}
                        </h6>
                        <div
                            className="fw-bold text-danger mb-0"
                            style={{ fontSize: 'clamp(0.9rem, 3vw, 1.3rem)', lineHeight: 1.2, whiteSpace: 'nowrap' }}
                        >
                            {showAmounts ? formatCurrency(totalExpenses) : '******'}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default FinancialSummaryCard;
